import React, { cloneElement, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import * as Styling from './StickyBar.styles'
import { useFeatureFlagContext } from '@/javascript/providers/FeatureFlagContext/FeatureFlagContext'

const BACK_TO_TOP_SELECTOR = '.pdp-v2-main [data-test="component-back-to-top"]'
const FEEDBACK_SELECTOR = '#csat-feedback a'
const STICKY_CTA_RAISED_CLASS = 'pdp-v2-cta-raised'

const StickyBar = ({ children, inView }) => {
  const { PDP_V2_FLAG } = useFeatureFlagContext()
  const itemList = Array.isArray(children) ? children : [children]
  const listCopy = itemList.map((item, key) => cloneElement(item, { key }))

  const handleBackToTopCTA = useCallback(() => {
    const backToTop = document.querySelector(BACK_TO_TOP_SELECTOR)
    if (!inView) {
      backToTop?.classList?.add(STICKY_CTA_RAISED_CLASS)
    } else {
      backToTop?.classList?.remove(STICKY_CTA_RAISED_CLASS)
    }
  }, [inView])

  const handleFeedbackCTA = useCallback(
    feedbackElement => {
      if (!inView) {
        feedbackElement?.classList?.add(STICKY_CTA_RAISED_CLASS)
      } else {
        feedbackElement?.classList?.remove(STICKY_CTA_RAISED_CLASS)
      }
    },
    [inView]
  )

  useEffect(() => {
    if (!PDP_V2_FLAG) return undefined

    handleBackToTopCTA()

    const feedbackElement = document.querySelector(FEEDBACK_SELECTOR)

    let observer

    if (feedbackElement) {
      handleFeedbackCTA(feedbackElement)
    } else {
      // Handle when feedback element loads in too late when sticky bar is already in view
      observer = new MutationObserver(() => {
        const observedFeedbackElement = document.querySelector(FEEDBACK_SELECTOR)
        if (observedFeedbackElement) {
          handleFeedbackCTA(observedFeedbackElement)
          observer.disconnect()
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
    }

    return () => {
      observer?.disconnect()
    }
  }, [PDP_V2_FLAG, inView])

  return (
    <Styling.Container inView={inView} PDP_V2_FLAG={PDP_V2_FLAG} data-test='sticky-bar'>
      {!inView && listCopy}
    </Styling.Container>
  )
}

StickyBar.propTypes = {
  children: PropTypes.node.isRequired,
  inView: PropTypes.bool.isRequired
}

export default StickyBar
